/**
 * Print barcodes only from a stored canonical barcode field.
 * Never fabricate a GTIN/barcode from SKU.
 */

const CODE128_PATTERNS = [
  "11011001100", "11001101100", "11001100110", "10010011000", "10010001100",
  "10001001100", "10011001000", "10011000100", "10001100100", "11001001000",
  "11001000100", "11000100100", "10110011100", "10011011100", "10011001110",
  "10111001100", "10011101100", "10011100110", "11001110010", "11001011100",
  "11001001110", "11011100100", "11001110100", "11101101110", "11101001100",
  "11100101100", "11100100110", "11101100100", "11100110100", "11100110010",
  "11011011000", "11011000110", "11000110110", "10100011000", "10001011000",
  "10001000110", "10110001000", "10001101000", "10001100010", "11010001000",
  "11000101000", "11000100010", "10110111000", "10110001110", "10001101110",
  "10111011000", "10111000110", "10001110110", "11101110110", "11010001110",
  "11000101110", "11011101000", "11011100010", "11011101110", "11101011000",
  "11101000110", "11100010110", "11101101000", "11101100010", "11100011010",
  "11101111010", "11001000010", "11110001010", "10100110000", "10100001100",
  "10010110000", "10010000110", "10000101100", "10000100110", "10110010000",
  "10110000100", "10011010000", "10011000010", "10000110100", "10000110010",
  "11000010010", "11001010000", "11110111010", "11000010100", "10001111010",
  "10100111100", "10010111100", "10010011110", "10111100100", "10011110100",
  "10011110010", "11110100100", "11110010100", "11110010010", "11011011110",
  "11011110110", "11110110110", "10101111000", "10100011110", "10001011110",
  "10111101000", "10111100010", "11110101000", "11110100010", "10111011110",
  "10111101110", "11101011110", "11110101110", "11010000100", "11010010000",
  "11010011100", "11000111010",
];

const START_B = 104;
const STOP = 106;

export function isPrintableCanonicalBarcode(barcode: string | null | undefined, sku?: string | null): boolean {
  const value = (barcode ?? "").trim();
  if (value.length < 4 || value.length > 128) return false;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 32 || code > 126) return false;
  }
  void sku;
  return true;
}

function code128BValue(char: string): number | null {
  const code = char.charCodeAt(0);
  if (code < 32 || code > 126) return null;
  return code - 32;
}

export function encodeCode128B(value: string): number[] | null {
  const codes: number[] = [START_B];
  let checksum = START_B;
  for (let i = 0; i < value.length; i += 1) {
    const symbol = code128BValue(value[i]!);
    if (symbol == null) return null;
    codes.push(symbol);
    checksum += symbol * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(STOP);
  return codes;
}

export function renderBarcodeSvg(barcode: string): string | null {
  const value = barcode.trim();
  const encoded = encodeCode128B(value);
  if (!encoded) return null;
  const bits = encoded.map((code) => CODE128_PATTERNS[code] ?? "").join("") + "11";
  if (!bits || /[^01]/.test(bits)) return null;
  const module = 1.2;
  const height = 28;
  const width = bits.length * module;
  const bars: string[] = [];
  let x = 0;
  let run = 0;
  let on = bits[0] === "1";
  for (const bit of bits) {
    const isOn = bit === "1";
    if (isOn === on) {
      run += 1;
      continue;
    }
    if (on) bars.push(`<rect x="${(x * module).toFixed(2)}" y="0" width="${(run * module).toFixed(2)}" height="${height}" />`);
    x += run;
    run = 1;
    on = isOn;
  }
  if (on && run) bars.push(`<rect x="${(x * module).toFixed(2)}" y="0" width="${(run * module).toFixed(2)}" height="${height}" />`);
  return `<svg class="barcode-svg" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${escapeAttr(value)}" viewBox="0 0 ${width.toFixed(2)} ${height}" width="${width.toFixed(1)}" height="${height}">${bars.join("")}</svg>`;
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

export function barcodePrintHtml(barcode: string | null | undefined, sku?: string | null): string {
  const value = (barcode ?? "").trim();
  if (!isPrintableCanonicalBarcode(value, sku)) return `<span class="barcode-none">—</span>`;
  const svg = renderBarcodeSvg(value);
  if (!svg) return `<span class="barcode-none">—</span>`;
  return `<div class="barcode">${svg}<div class="barcode-text">${escapeAttr(value)}</div></div>`;
}
